import styled from 'styled-components';

import SelfieLeft from 'images/inquiry/animations/selfie-left.json';
import SelfieRight from 'images/inquiry/animations/selfie-right.json';
import CustomAssetWithFallback from 'inquiry/components/CustomAssetWithFallback';
import ThemedLottie from 'inquiry/components/ThemedLottie/ThemedLottie';
import { colorCss } from 'lib/inquiryStyles';
import { SerializedStepSelfieStyles, StepSelfieConfig } from 'lib/interfaces/StepsConfig';
import { assertNever } from 'lib/utils/types';
import { SelfieStep } from 'verify/lib/interfaces/selfie';

const Positioner = styled('div')`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;

  display: flex;
  align-items: center;
  justify-content: center;
`;

interface WrapperProps {
  isVisible: boolean;
  profileSize: number;
}

const Wrapper = styled('div')<WrapperProps>`
  display: flex;
  align-items: center;
  justify-content: center;

  width: ${({ profileSize }) => profileSize}px;
  height: ${({ profileSize }) => profileSize}px;
`;

const StyledThemedLottie = styled(ThemedLottie)<{ $profileSize: number }>`
  svg {
    width: ${({ $profileSize }) => $profileSize}px !important;
    height: ${({ $profileSize }) => $profileSize}px !important;
  }
`;

interface DirectionalHintProps {
  assets?: StepSelfieConfig['assets']['record-page'];
  isActive: boolean;
  profileSize: number;
  currentStep: SelfieStep | undefined;
  styles?: SerializedStepSelfieStyles['captureIcon'];
}

export default function DirectionalHint({
  assets,
  isActive,
  profileSize,
  currentStep,
  styles,
}: DirectionalHintProps) {
  const isVisible = isActive && !!currentStep;

  let prompt;
  switch (currentStep) {
    case SelfieStep.Left:
      prompt = (
        <CustomAssetWithFallback customImage={assets?.['selfie-left-pictograph']} alt='look left'>
          <StyledThemedLottie
            role='img'
            aria-label='look left'
            animationData={SelfieLeft}
            loop={false}
            $profileSize={profileSize}
            $primaryColor={colorCss(styles?.fill?.color)}
            // Note that what SST calls 'background' is actually used as 'highlight' here
            $highlightColor={colorCss(styles?.background?.color)}
            $strokeColor={colorCss(styles?.stroke?.color)}
          />
        </CustomAssetWithFallback>
      );
      break;

    case SelfieStep.Right:
      prompt = (
        <CustomAssetWithFallback customImage={assets?.['selfie-right-pictograph']} alt='look right'>
          <StyledThemedLottie
            role='img'
            aria-label='look right'
            animationData={SelfieRight}
            loop={false}
            $profileSize={profileSize}
            $primaryColor={colorCss(styles?.fill?.color)}
            // Note that what SST calls 'background' is actually used as 'highlight' here
            $highlightColor={colorCss(styles?.background?.color)}
            $strokeColor={colorCss(styles?.stroke?.color)}
          />
        </CustomAssetWithFallback>
      );
      break;

    case SelfieStep.Center:
      break;

    // When the user has completed the verification
    case undefined:
      break;

    default:
      assertNever(currentStep);
  }

  return (
    <Positioner>
      <Wrapper isVisible={isVisible} profileSize={profileSize}>
        {prompt}
      </Wrapper>
    </Positioner>
  );
}
